import type { ProductConfig } from './product/product.types';
import type { InstallmentCalculatorConfig } from './product/installmentCalculator.types';
import type { CustomerRatingsConfig } from './product/customerRatings.types';
import type { OrderConfig } from './order/order.types';
import type { DynamicHelpConfig } from './cms/dynamicHelp.types';

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[P] extends ReadonlyArray<infer U>
      ? ReadonlyArray<DeepPartial<U>>
      : DeepPartial<T[P]>;
};

export type CookieCategory = 'C0001' | 'C0003' | 'C0002' | 'C0004' | 'C0005' | 'C0006';

export type GeoCountryCode = string | undefined;
export type Flags = 'de' | 'ch' | 'fr' | 'at' | 'it' | 'nl';
export type Language = 'de' | 'fr' | 'en' | 'nl';
export type Locale = 'de-DE' | 'fr-CH' | 'de-CH' | 'de-AT' | 'nl-NL';

export type PreRenderAtBuldTimeVariants = 'type' | 'cmsPage';

export enum Environment {
  STAGING = 'staging',
  PRODUCTION = 'production',
}

export type Config = {
  host: {
    name: string;
    domain: string;
    appDeeplinkProtocol?: string;
  };
  i18n: {
    defaultLocale: Locale;
    languages: Language[];
    locales: Locale[];
  };
  clientId: string;
  tenantId: string;
  styleVariant: 'default' | 'dark';
  company: {
    name: string;
    seoTitleName: string;
  };
  timeZone: string;
  captcha: {
    recaptchaSessionToken: string;
  };
  assets: {
    cmsImage: {
      imageQuality: number;
      imageOptimizationEnabled: boolean;
      uriTransformSource: RegExp;
      uriTransformEndpoint: string;
    };
    assetServerHost: string;
  };
  middleware: {
    cmsRewrites: {
      exact: string[];
      prefixes: string[];
    };
  };
  apiEndpoints: {
    search: string;
    searchFilter: string;
    searchSeo: string;
    suggest: string;
    historyGet: string;
    historyApiNew: string;
    historyDelete: string;
    historyDeleteAll: string;
    historyDeleteAllNew: string;
    historyJoinWithFi: string;
    customer: string;
  };
  insiderPushNotifications: {
    enabled: boolean;
    apiKey?: string;
    partnerName?: string;
    partnerId?: number;
  };
  dynamicYieldEnabled: boolean;
  hermesShopFinder: HermesShopFinderConfig;
  product: ProductConfig;
  installmentCalculator: InstallmentCalculatorConfig;
  header: HeaderConfig;
  usp: UspConfig;
  search: SearchConfig;
  order: OrderConfig;
  staticContent: StaticContentConfig;
  footer: FooterConfig;
  banner: BannerConfig;
  nlSheetTexts: NlSheetTextsConfig;
  serviceLayer: ServiceLayerConfig;
  seoContent: SeoContentConfig;
  survey: SurveyConfig;
  sovendus: {
    enabled: boolean;
  };
  chatBot: ChatBotConfig;
  promotionBanner: PromotionBannerConfig;
  voucher: VoucherConfig;
  recommendations: RecommendationsConfig;
  searchResultUserFeedback: FeedbackConfig;
  shoppingApp: {
    pathsToRenderChildrenOnly: string[];
  };
  navigation: {
    firstLevel: {
      color: string;
      backgroundColor?: string;
      border: string;
    };
    secondLevel?: {
      color: string;
      backgroundColor?: string;
      border: string;
    };
    loadSliced: boolean;
    firstLevelFont?: string;
    firstLevelFontSize?: string;
    dataBucket: string;
  };
  forms: {
    useOptimizely: boolean;
    newsletterSettings: {
      enabled: boolean;
      environmentRelated: boolean;
      useDOI: boolean;
      sendAdditionalSubscribe: boolean;
      useApproval: boolean;
    };
    contact: {
      dynamicHelp: DynamicHelpConfig;
    };
    newsletterUnsubscribe: {
      fields: CmsFormsFieldConfig[];
      useDedicatedSubmitButtonTitleFor?: string[];
    };
    newsletterSubscribe: Record<Language, { unsubscribeLink: string; voucherLink: string }>;
    braFinder: Record<Language, { searchLink: string }>;
    newsletterSheet: NewsletterSheetConfig;
    validation: {
      rules: {
        customerId: {
          regex: RegExp;
          errorMessage: {
            id: string;
            defaultMessage: string;
          };
        };
        postalCode: {
          regex: RegExp;
          errorMessage: {
            id: string;
            defaultMessage: string;
          };
        };
        fileUpload: {
          fileLimit: number;
          maxFileSizeMb: number;
          maxFileSizeSummaryMb: number;
          acceptedFormats: string;
          regexToCheckImageFormats: RegExp;
          regexAcceptedUploadFormats: RegExp;
          indicators: {
            showFileSize: boolean;
            showFileSizeSummary: boolean;
          };
        };
      };
    };
  };
  enableCustomerNrLogin: boolean;
  headerSlots: {
    localeSwitcher: boolean;
    payback: boolean;
    joe: boolean;
    serviceApi?: string;
  };
  headerLinks: Record<
    Locale,
    {
      serviceLink: string;
    }
  >;
  appBanner: {
    enabled: boolean;
    appUrl: string;
  };
  tracking: TrackingConfig;
  abTesting: AbTestingConfig;
  testPanel: TestPanelConfiguration;
  externalScript?: {
    kuecheCo?: {
      key: string;
    };
  };
  video: {
    video_youtube: { url: string; provider: string; name: string };
  };
  headerInformation: Partial<
    Record<
      Language,
      {
        description: string;
        title: string;
        canonical: string;
        robots: string;
      }
    >
  >;
  customerRatings: CustomerRatingsConfig;
  customerCentricFeedback: {
    enabled: boolean;
    tenantId: string;
    surveyIdStorefront: string;
    surveyIdSearch: string;
    surveyIdDetailview: string;
  };
  dynamicYieldTracking: {
    key: string;
    endpoint: string;
    endpointClientSide: string;
  };
  officeTimes: Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', string>>;
  geoCountryLayer: {
    enabled: boolean;
    flag?: Flags;
    img: {
      path: string;
      alt: string;
      title: string;
    };
    alternatives?: AlternativesShops[];
  };
  redirectOverlay: {
    enabled: boolean;
    redirectParam: string;
    redirectLp: {
      de: string;
      fr?: string;
    };
  };
  lounge: {
    icon?: (lang: string) => string;
  };
  bookings: {
    overview: {
      hasExplicitMaturities: boolean;
      showOpenPayment: 'none' | 'minimal-payment' | 'installments' | 'sum-and-open';
    };
  };
  priceFormat: PriceFormat;
  confettiEnabled: boolean;
  speedKit: {
    enabled: boolean;
    scriptUrl?: string;
  };
  // This configuration is used for tenants that are in the process of closing and require certain UI elements to be hidden.
  // Specifically, it allows hiding the search field, navigation, and the basket, wishlist, and search slots.
  isTenantClosing: boolean;
};

export type InternalPageMetadata = {
  title?: string;
};

export type SchemaOrgOrganisation = Record<
  Locale,
  {
    addressCountry: string;
    addressRegion: string;
    addressLocality: string;
    alternateName: string[];
    areaServed: string;
    contactEmail: string;
    contactUrl: string;
    description: string;
    email: string;
    founder?: string;
    foundingDate?: string;
    foundingLocation?: string;
    hoursAvailable: string[];
    legalName: string;
    logo: string;
    memberOf?: string;
    name: string;
    postalCode: string;
    privacyNoticePath: string;
    potentialAction: {
      target: string;
    };
    productSupported: string;
    sameAs: string[];
    slogan?: string;
    streetAddress: string;
    telephone: string;
    url: string;
    vatID?: string;
  }
>;

export type AlternativesShops = {
  flag?: Flags;
  shops?: AlternativesShopsItem[];
};

export type AlternativesShopsItem = {
  link?: string;
  title?: string;
  closeLayer?: boolean;
};

export interface TrackingConfig {
  categoryIdHomepage: string;
  soastaId: string;
  tagmanagerId: string;
  tagmanagerDomain?: string;
  /** Full script url. Overwrites tagmanagerId and tagmanagerDomain config */
  tagmanagerSrc?: string;
  googleSiteVerificationID?: string[];
  msvalidateID?: string;
  schemaOrgOrganisation: SchemaOrgOrganisation;
  pinterestRichPinVerificationID?: string;
}

export interface AbTestingConfig {
  serverSideEnrollment: {
    /**
     * List of all active test ids all users should be automatically enrolled in on the server-side.
     *
     * Enrollment on the server side has the benefits that it can be used for data fetching even on the first request, but the drawbacks that it will delay the first response on a new session/visit to fetch the info.
     */
    activeTestIds: string[];
  };
  clientSideEnrollment: {
    /**
     * List of all active test ids all users should be automatically enrolled in on the client-side.
     *
     * Enrollment on the client side has the benefits that it will not delay the first response on a new session/visit, but the drawbacks that it can not be used for data fetching on the first request.
     */
    activeTestIds: string[];
  };
}

export interface TestPanelConfiguration {
  apps?: TestPanelApp[];
  backends?: TestPanelApp[];
  systems?: TestPanelSystem[];
}

export interface TestPanelApp {
  appLabel: string;
  appName: string;
  defaultBranch: string;
  projectId: string;
  featureCookieName: string;
  hasPathQuerySyntax?: boolean;
}

export interface TestPanelSystem {
  appLabel: string;
  appName: string;
  connector: 'apiKeyManager';
  systems: Record<string, string>;
  featureCookieName?: string;
  defaultBranch?: string;
}

export interface ExternalService {
  apiKey?: string;
  apiUrl?: string;
  consentGroup?: CookieCategory;
  containerName?: string;
  customerId?: string;
  debugEnabled?: boolean;
  enabled?: boolean;
  moreInformationLink?: string;
  scriptUrl?: string;
}

export type PersonalizationSegmentSource = 'legacy' | 'baur' | 'unito';

export type AdServerDisabledConfig = {
  mode: 'disabled';
  mkzBlacklist: number[];
};

export type AdServerContainerOnlyConfig = {
  mode: 'containerOnly';
  mkzBlacklist: number[];
};

export type AdServerDoubleClickConfig = {
  mode: 'doubleClick';
  adServerClientId: string;
  adBlockName: string;
  mkzBlacklist: number[];
};

export type AdServerConfig =
  | AdServerDisabledConfig
  | AdServerContainerOnlyConfig
  | AdServerDoubleClickConfig;

export type SearchConfig = {
  personalizationSegmentSource: PersonalizationSegmentSource;
  adServer: AdServerConfig;
  hideTelekomIcon: boolean;
  /**
   * When set to 'loadMore', the pagination will be removed and only an endless list (with manual "load more") will be available.
   * When set to 'paginationAndEndlessScrolling', the pagination will be available, and the user can optionally switch to automatic endless scrolling;
   */
  paginationMode: 'loadMore' | 'paginationAndEndlessScrolling';
  /** Without trailing slash, so append full pathnames like `/` or `/foo.json` */
};

export type StaticContentConfig = {
  fetchSettings: {
    timeout: number;
  };
  apiEndpoints: {
    jsonApi: string;
    bucket: string;
    webappJson: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type HeaderConfig = {
  hasSmallerFontSize: boolean;
  desktopNavigationMode: 'click' | 'hover';
};

export type UspConfig = {
  isEnabled: boolean;
};

export type FooterConfig = {
  fetchSettings: {
    timeout: number;
  };
  apiEndpoints: {
    jsonApi: string;
    bucket: string;
    deDupingInterval: number;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type RecommendationsConfig = {
  apiEndpoint: string;
  provider: 'prudsys' | 'empiriecom';
};

export type FeedbackConfig = {
  appUrl?: string;
  enabled: boolean;
};

export type BannerConfig = {
  apiEndpoints: {
    bucket: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type NlSheetTextsConfig = {
  apiEndpoints: {
    bucket: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type ServiceLayerConfig = {
  apiEndpoints: {
    api: string;
    bucket: string;
    bucketPages: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type SurveyConfig = {
  apiEndpoints: {
    bucket: string;
  };
};

export type HermesShopFinderConfig = {
  url: string;
  rParam: string;
};

export type NewsletterSheetConfig = {
  enabled: boolean;
};

export type ChatBotConfig = {
  enabled: boolean;
  key: string;
  includePaths: string[];
  excludePaths: string[];
  visibility: {
    main: boolean;
    button: boolean;
    notifications: boolean;
  };
};

export type SeoContentConfig = {
  apiEndpoints: {
    bucket: string;
  };
};

export type PromotionBannerConfig = {
  apiEndpoints: {
    jsonApi: string;
    bucketPromotion: string;
    bucketEmptyPromotions: string;
    bucketContentSnippet: string;
  };
  abTest?: string;
};

export type CmsFormsFieldOptionsConfig = {
  id: string;
  value: string;
  messageKey: string;
};

export type CmsFormsFieldConfig = {
  id: string;
  type?: string;
  defaultValue?: string;
  options?: CmsFormsFieldOptionsConfig[];
  formType?: 'input' | 'radio' | 'select' | 'text' | 'email' | 'number' | 'password' | 'date';
  messageKeyPlaceholder?: string;
  additionalForm?: CmsFormsFieldConfig;
  showOnlyWhenWhatSelected?: string;
};

export type VoucherConfig = {
  apiEndpoints: {
    api: string;
    bucket: string;
  };
};

export type LocalizedPath = Partial<Record<Locale, string>> & {
  default: string;
};

export type PriceFormat = 'Empiriecom' | 'NoDelimiterCurrencyAtTheEnd' | 'Iso';
